import { cloneDeep } from "es-toolkit";
import { computed, onUnmounted, reactive, type Ref, ref, toValue, useId, watchEffect } from "vue";
import { alerts } from "../common/alert.common";
import type { BaseProps } from "./props";

type ReturnValue<T extends undefined | ((...args: any) => any)> = ReturnType<NonNullable<T>>;

export function defineState<T, P extends BaseProps<NonNullable<T>>>({
    model,
    props,
    hasValue,
}: {
    model: Ref<T>;
    props: P;
    hasValue: () => boolean;
}) {
    const editModeState = ref(props.mode === "create");
    const editMode = computed({
        get() {
            return editModeState.value;
        },
        set(newValue: boolean) {
            editModeState.value = newValue;
        },
    });

    watchEffect(() => {
        editMode.value = props.mode === "create";
    });

    return reactive({
        id: useId(),
        original: ref(model.value),
        resetVal: ref<T | undefined>(),
        editMode: editMode,
        hasError: ref(false),
        hasValue: computed(hasValue),
        errorMsg: ref<string>(),
        nestMode: computed(() => (editMode.value ? "create" : "static")),
        fn: {
            generate: ref<ReturnValue<typeof props.attachGenerate>["generate"]>(),
            validate: ref<ReturnValue<typeof props.attachValidate>["validate"]>(),
            scramble: ref<ReturnValue<typeof props.attachScramble>["scramble"]>(),
        },
        clearable: computed(
            () => props.attachReset !== undefined && props.clearable && !props.disabled,
        ),
        deletable: computed(
            () => props.attachRemove !== undefined && props.deletable && model.value !== undefined,
        ),
        updatable: computed(() => props.embedMode === undefined && props.mode === "update"),
        removable: computed(
            () =>
                props.embedMode === undefined &&
                model.value !== undefined &&
                props.mode === "remove",
        ),
        generatable: computed(() => props.attachGenerate !== undefined && editMode.value === true),
        randomizable: computed(() => props.attachScramble !== undefined && editMode.value === true),
    });
}

export function defineActions<T, P extends BaseProps<NonNullable<T>>>(
    model: Ref<T>,
    props: P,
    state: ReturnType<typeof defineState<T, P>>,
    actions?: Partial<ReturnType<typeof defaultActions>>,
) {
    const currentActions = {
        ...defaultActions(model, props, state),
        ...actions,
    };

    if (props.value !== undefined) {
        model.value = cloneDeep(props.value);
    }

    if (props.attachReset !== undefined) {
        const hook = props.attachReset(currentActions.onReset);
        state.resetVal = hook.resetVal;
        onUnmounted(hook.detachFn);
    }

    if (props.attachScramble !== undefined) {
        const hook = props.attachScramble(currentActions.onScramble);
        state.fn.scramble = hook.scramble;
        onUnmounted(hook.detachFn);
    }

    if (props.attachGenerate !== undefined) {
        const hook = props.attachGenerate(currentActions.onGenerate);
        state.fn.generate = hook.generate;
        onUnmounted(hook.detachFn);
    }

    if (props.attachValidate !== undefined) {
        const hook = props.attachValidate(currentActions.onValidate);
        state.fn.validate = hook.validate;
        onUnmounted(hook.detachFn);
    }

    return currentActions;
}

function defaultActions<T, P extends BaseProps<NonNullable<T>>>(
    model: Ref<T | undefined>,
    props: P,
    state: ReturnType<typeof defineState<T, P>>,
) {
    return {
        onCancel() {
            state.hasError = false;
            state.editMode = false;
            model.value = cloneDeep(toValue(state.original)) as T;
        },
        async onDelete() {
            if (props.attachRemove !== undefined) {
                if (await alerts.form.confirmRemove(props.label)) {
                    if (await props.attachRemove()) {
                        model.value = undefined;
                    }
                }
            }
        },
        async onSubmit() {
            if (!state.hasValue) return;
            if (
                props.attachUpdate !== undefined &&
                model.value !== undefined &&
                model.value !== null
            ) {
                if (await props.attachUpdate(model.value)) {
                    state.editMode = false;
                }
            }
        },
        onUpdate() {
            state.original = cloneDeep(toValue(model.value)) as typeof state.original;
            state.editMode = true;
        },
        async onGenerate() {
            if (state.editMode && state.fn.generate !== undefined) {
                model.value = await state.fn.generate();
            }
        },
        async onValidate() {
            state.hasError = false;
            if (state.fn.validate === undefined) return true;

            const result = await state.fn.validate(model.value);
            if (!result.success) {
                state.hasError = true;
                state.errorMsg = result.issues[0].message;
            }

            return result.success;
        },
        onReset() {
            if (!state.editMode) return;
            model.value = cloneDeep(state.resetVal);
            state.hasError = false;
        },
        onScramble() {
            if (props.disabled === true) return;
            if (state.editMode && state.fn.scramble !== undefined) {
                model.value = state.fn.scramble();
            }
        },
    } as const;
}
